/**
 * Pretty-prints a phone number as `+CC AREA NUMBER`.
 *
 * The smxSystems Dnis table stores numbers in plain E.164 form (without
 * the leading `+`) and the country/area code as separate string columns.
 * Crucially, the area code in our schema is stored *with* the country
 * prefix in front (e.g. NL Amsterdam → countryCode "31" + areaCode
 * "3120" — i.e. the area code is "3120" not "20"). This helper unwinds
 * that so the user sees the natural human form: "31201234567" →
 * "+31 20 1234567".
 *
 * Falls back gracefully when the country/area code can't be applied to
 * the leading digits of the number — in that case we return the raw
 * number unchanged so we never present misleading information.
 */
export function formatPhoneNumber(
  raw: string | null | undefined,
  countryCode?: string | null,
  areaCode?: string | null,
): string {
  const digits = onlyDigits(raw);
  if (!digits) return raw ?? "";

  const cc = onlyDigits(countryCode);
  const ac = onlyDigits(areaCode);

  // Strip the country code from the front of the number when present,
  // so the remainder is "national digits".
  let remainder = digits;
  if (cc && remainder.startsWith(cc)) {
    remainder = remainder.slice(cc.length);
  }

  // Strip the leading country-prefix from the area code: in our schema
  // areaCode "3120" is "31" (country) + "20" (subscriber area). We only
  // want the "20" part to render between the country prefix and the
  // subscriber number.
  let nationalArea = ac;
  if (cc && nationalArea.startsWith(cc)) {
    nationalArea = nationalArea.slice(cc.length);
  }

  // When the national area is genuinely usable AND it lines up with the
  // start of the remaining digits, peel it off so we can render
  // `+CC AREA NUMBER`.
  if (nationalArea && remainder.startsWith(nationalArea)) {
    const subscriber = remainder.slice(nationalArea.length);
    if (cc) {
      return subscriber
        ? `+${cc} ${nationalArea} ${subscriber}`
        : `+${cc} ${nationalArea}`;
    }
    return subscriber ? `${nationalArea} ${subscriber}` : nationalArea;
  }

  // No usable area split — show country + remainder if we have CC, or
  // fall back to the bare number so we never mangle it.
  if (cc) return remainder ? `+${cc} ${remainder}` : `+${cc}`;
  return raw ?? digits;
}

function onlyDigits(s: string | null | undefined): string {
  if (!s) return "";
  return s.replace(/\D/g, "");
}

/**
 * Normalises free-form input to the digits-only E.164 form used by the
 * Dnis table ("+31 (0)10-123 45 67" → "310101234567", "0031…" → "31…").
 * Every non-digit is dropped and a leading international "00" prefix is
 * removed. Returns null when the result is not a plausible number
 * (6..15 digits).
 */
export function normalizeToE164Digits(raw: string | null | undefined): string | null {
  let digits = onlyDigits(raw);
  if (digits.startsWith("00")) digits = digits.slice(2);
  return digits.length >= 6 && digits.length <= 15 ? digits : null;
}

/**
 * Splits a pasted list of numbers (one per line, or comma / semicolon
 * separated), normalises each entry and removes duplicates while keeping
 * the input order. Spaces, dashes and brackets inside a number are
 * allowed ("+31 10 123 45 67"). `invalid` holds the raw tokens that did
 * not normalise.
 */
export function parseNumberList(text: string): {
  numbers: string[];
  invalid: string[];
  duplicates: number;
} {
  const numbers: string[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();
  let duplicates = 0;
  for (const token of text.split(/[\n\r,;]+/)) {
    const t = token.trim();
    if (!t) continue;
    const n = normalizeToE164Digits(t);
    if (!n) {
      invalid.push(t);
      continue;
    }
    if (seen.has(n)) {
      duplicates += 1;
      continue;
    }
    seen.add(n);
    numbers.push(n);
  }
  return { numbers, invalid, duplicates };
}

/** One line of the "manager, customer, number, provider" import format. */
export interface NumberAssignmentEntry {
  number: string;
  managerName: string;
  customerName: string;
  /** Provider name from the line; empty when the line had no provider column. */
  providerName: string;
}

/**
 * Parses the extended import format, one entry per line:
 * `managerName, customerName, number, provider` (comma, semicolon or tab
 * separated; the provider column is optional).
 * Plain number lines (one column) are still accepted, but the two formats
 * cannot be mixed: `mixed` is set when both occur and the caller should
 * refuse the input. `assignments` is true when every line carries a
 * manager and customer. Numbers are normalised and de-duplicated like
 * {@link parseNumberList}; the first occurrence of a number wins.
 */
export function parseNumberImportInput(text: string): {
  assignments: boolean;
  mixed: boolean;
  entries: NumberAssignmentEntry[];
  numbers: string[];
  invalid: string[];
  duplicates: number;
} {
  const entries: NumberAssignmentEntry[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();
  let duplicates = 0;
  let plainLines = 0;
  let assignedLines = 0;
  for (const rawLine of text.split(/[\n\r]+/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const cols = line.split(/[\t;,]/).map((c) => c.trim());
    if (cols.length >= 3) {
      // manager, customer, number[, provider]. The number is the last column
      // that parses as a number (so a trailing provider column is optional),
      // the manager is the first column and the customer everything between.
      const managerName = cols[0];
      let numberIdx = cols.length - 1;
      let providerName = "";
      if (cols.length >= 4 && normalizeToE164Digits(cols[cols.length - 1]) === null) {
        providerName = cols[cols.length - 1];
        numberIdx = cols.length - 2;
      } else if (cols.length >= 4 && normalizeToE164Digits(cols[cols.length - 2]) !== null && !/^\d+$/.test(cols[cols.length - 1])) {
        providerName = cols[cols.length - 1];
        numberIdx = cols.length - 2;
      }
      const n = normalizeToE164Digits(cols[numberIdx]);
      const customerName = cols.slice(1, numberIdx).join(", ").trim();
      if (!n || !managerName || !customerName) {
        invalid.push(line);
        continue;
      }
      assignedLines += 1;
      if (seen.has(n)) {
        duplicates += 1;
        continue;
      }
      seen.add(n);
      entries.push({ number: n, managerName, customerName, providerName });
      continue;
    }
    // Plain numbers: a line may hold several, comma or semicolon separated.
    for (const token of cols) {
      if (!token) continue;
      const n = normalizeToE164Digits(token);
      if (!n) {
        invalid.push(token);
        continue;
      }
      plainLines += 1;
      if (seen.has(n)) {
        duplicates += 1;
        continue;
      }
      seen.add(n);
      entries.push({ number: n, managerName: "", customerName: "", providerName: "" });
    }
  }
  return {
    assignments: assignedLines > 0 && plainLines === 0,
    mixed: assignedLines > 0 && plainLines > 0,
    entries,
    numbers: entries.map((e) => e.number),
    invalid,
    duplicates,
  };
}
